import {useEffect, useState} from 'react'
import {createUseStyles} from 'react-jss'
import Modal from '../Modal'
import {
  CompanyGroupPortalUser,
  CreateCompanyGroupPortalUserModel,
  UpdateCompanyGroupPortalUserModel,
  CompanyGroupPortalRoleModel
} from '../../redux/types'
import InputField from '../InputField'
import Switch from '../Switch'
import {useTranslation} from 'react-i18next'

interface Props {
  initialValue: CompanyGroupPortalUser | undefined
  portalRoles: CompanyGroupPortalRoleModel[]
  onCreate: (createCustomerPortalUserModel: CreateCompanyGroupPortalUserModel) => void
  onUpdate: (updateCustomerPortalUserModel: UpdateCompanyGroupPortalUserModel) => void
  onCancelClick: () => void
  visible: boolean
}

const EditCompanyGroupPortalUserModal = ({
                                       initialValue,
                                       portalRoles,
                                       onCreate,
                                       onUpdate,
                                       onCancelClick,
                                       visible
                                     }: Props) => {
  const styles = useStyles()
  const {t} = useTranslation(['common', 'portalUsers']);
  const [identificationNumber, setIdentificationNumber] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [portalRoleIds, setPortalRoleIds] = useState<string[]>([])
  const [isContactTechnical, setIsContactTechnical] = useState<boolean>(false)
  const [isContactFinance, setIsContactFinance] = useState<boolean>(false)
  const [isContactPayroll, setIsContactPayroll] = useState<boolean>(false)

  const isEditing = !!initialValue
  const title = isEditing ? t('editPortalUser', {ns: 'portalUsers'}) : t('addPortalUser', {ns: 'portalUsers'})

  const setState = (companyGroupPortalUser: CompanyGroupPortalUser | undefined) => {
    if (companyGroupPortalUser) {
      setIdentificationNumber(companyGroupPortalUser.identificationNumber)
      setEmail(companyGroupPortalUser.email)
      setPortalRoleIds(companyGroupPortalUser.portalRoles.map(x => x.id))
    } else {
      setIdentificationNumber('')
      setEmail('')
      setPortalRoleIds([])
      setIsContactTechnical(false)
      setIsContactFinance(false)
      setIsContactPayroll(false)
    }
  }

  useEffect(() => {
    setState(initialValue)
  }, [initialValue])

  const isSaveable = () => {
    return isEditing || (identificationNumber.length > 0 && email.length > 0)
  }

  const handleOnSave = () => {
    if (isEditing) {
      onUpdate({
        portalRoleIds: portalRoleIds,
        isContactFinance: isContactFinance,
        isContactTechnical: isContactTechnical,
        isContactPayroll: isContactPayroll
      } as UpdateCompanyGroupPortalUserModel)
    } else {
      onCreate({
        identificationNumber: identificationNumber,
        email: email,
        portalRoleIds: portalRoleIds,
        isContactFinance: isContactFinance,
        isContactTechnical: isContactTechnical,
        isContactPayroll: isContactPayroll
      } as CreateCompanyGroupPortalUserModel)
    }

    setState(undefined)
  }

  const handleOnCancel = () => {
    onCancelClick()
    setState(undefined)
  }

  const handleRoleChange = (portalRoleModel: CompanyGroupPortalRoleModel, value: boolean) => {
    if (value) {
      setPortalRoleIds([...portalRoleIds, portalRoleModel.id])
    } else {
      setPortalRoleIds(portalRoleIds.filter(x => x !== portalRoleModel.id))
    }
  }

  const renderRoleSwitch = (portalRoleModel: CompanyGroupPortalRoleModel) => {
    return (
      <Switch
        id={portalRoleModel.id}
        key={portalRoleModel.id}
        checked={portalRoleIds.includes(portalRoleModel.id)}
        label={portalRoleModel.name}
        onChange={() => handleRoleChange(portalRoleModel, !portalRoleIds.includes(portalRoleModel.id))}
      />
    )
  }

  return (
    <Modal
      title={title}
      visible={visible}
      okButtonDisabled={!isSaveable()}
      okButtonTitle={t('save', {ns: 'common'})}
      cancelButtonTitle={t('cancel', {ns: 'common'})}
      onHide={() => handleOnCancel()}
      onOkClick={() => handleOnSave()}
      onCancelClick={() => handleOnCancel()}
    >
       <div className={styles.row}>
        <InputField
          disabled={isEditing}
          mandatory={!isEditing}
          id='identificationNumber'
          value={initialValue?.identificationNumber}
          onChange={(value) => setIdentificationNumber(value)}
          label={t('identificationNumber', {ns: 'common'})}
          placeholder=''
        />
      </div>
      <div className={styles.row}>
        <InputField
          disabled={isEditing}
          mandatory={!isEditing}
          id='email'
          value={initialValue?.email}
          onChange={(value) => setEmail(value)}
          label={t('email', {ns: 'common'})}
          placeholder=''
        />
      </div>

      <div className={styles.row}>
        <h5 style={{marginTop: 20, marginBottom: 10}}>{t('roles', {ns: 'portalUsers'})}</h5>
        {portalRoles.map(x => renderRoleSwitch(x))}
      </div>
    </Modal>
  )
}

export default EditCompanyGroupPortalUserModal

const useStyles = createUseStyles({
  row: {
    paddingBottom: 12
  }
})
